import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import RequirePermission from "../../../auth/RequirePermission";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import { fetchInventoryQualityDashboard, recalculateInventoryQuality } from "../../../services/automapInventoryQualityApi";
import { trackInventoryQualityEvent } from "../../../constants/automapInventoryQualityConstants";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import { InventoryQualityNav } from "./inventoryQualityShared";

function MetricCard({ label, value, href }) {
  const content = (
    <div className="admin-dashboard__metric-card">
      <span className="admin-dashboard__metric-label">{label}</span>
      <strong className="admin-dashboard__metric-value">{value ?? "—"}</strong>
    </div>
  );
  return href ? <Link to={href}>{content}</Link> : content;
}

function AdminInventoryQualityDashboardPageContent() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [recalculating, setRecalculating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchInventoryQualityDashboard();
      setSummary(data.summary);
      trackInventoryQualityEvent("inventory_quality_dashboard_viewed");
    } catch (err) {
      setError(err.message || "No se pudo cargar el dashboard de calidad.");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const recalculate = async () => {
    setRecalculating(true);
    try {
      await recalculateInventoryQuality({});
      trackInventoryQualityEvent("inventory_quality_score_recalculated");
      await load();
    } catch (err) {
      setError(err.message || "Recálculo fallido.");
    } finally {
      setRecalculating(false);
    }
  };

  const metrics = summary?.metrics || {};

  return (
    <AdminPageLayout className="admin-inventory-quality-page">
      <AdminAutomotiveNav active="inventory-quality" />
      <AdminPageHeader
        eyebrow="AutoMapa · Calidad de inventario"
        title="Calidad de inventario"
        description="Métricas de frescura, completitud y confianza del marketplace."
        navigation={<InventoryQualityNav active="dashboard" />}
        primaryActions={
          <button type="button" className="admin-btn admin-btn--primary" disabled={recalculating} onClick={recalculate}>
            {recalculating ? "Recalculando…" : "Recalcular calidad"}
          </button>
        }
      />
      <main className="admin-console__main">
        {error ? <AdminStatePanel variant="empty" message={error} onRetry={load} retryLabel="Reintentar" /> : null}
        {loading ? (
          <AdminStatePanel variant="empty" message="Cargando métricas…" />
        ) : summary ? (
          <>
            <div className="admin-dashboard__metric-grid">
              <MetricCard label="Activos" value={metrics.totalActive} href="/admin/listings?status=active" />
              <MetricCard label="Confirmados (7d)" value={metrics.confirmedWithin7Days} href="/admin/inventory-quality/stale" />
              <MetricCard label="Obsoletos" value={metrics.stale} href="/admin/inventory-quality/stale" />
              <MetricCard label="Sin fotos" value={metrics.missingPhotos} href="/admin/inventory-quality/incomplete?issueGroup=missing_photos" />
              <MetricCard label="Sin precio" value={metrics.missingPrice} href="/admin/inventory-quality/incomplete" />
              <MetricCard label="Sin km" value={metrics.missingMileage} href="/admin/inventory-quality/incomplete" />
              <MetricCard label="Sin descripción" value={metrics.missingDescription} href="/admin/inventory-quality/incomplete?issueGroup=missing_description" />
              <MetricCard label="Calidad baja" value={metrics.lowQuality} href="/admin/inventory-quality/incomplete" />
              <MetricCard label="Duplicados" value={metrics.duplicateCandidates} href="/admin/inventory-quality/duplicates" />
              <MetricCard label="Atípicos precio" value={metrics.priceOutliers} href="/admin/inventory-quality/outliers" />
              <MetricCard label="Atípicos km" value={metrics.mileageOutliers} href="/admin/inventory-quality/outliers" />
              <MetricCard label="Dealers obsoletos" value={metrics.dealersWithStaleInventory} href="/admin/inventory-quality/dealer-followups" />
              <MetricCard label="Dealers calidad baja" value={metrics.dealersWithLowQuality} href="/admin/inventory-quality/dealer-followups" />
              <MetricCard label="Calidad promedio" value={metrics.averageQualityScore != null ? `${metrics.averageQualityScore}/100` : "—"} />
              <MetricCard label="Cobertura fotos" value={`${metrics.photoCoveragePct ?? 0}%`} />
              <MetricCard label="Frescura" value={`${metrics.freshnessPct ?? 0}%`} />
              <MetricCard label="% obsoleto" value={`${metrics.staleListingRatePct ?? 0}%`} />
              <MetricCard label="Seguimientos abiertos" value={metrics.openFollowUps} href="/admin/inventory-quality/dealer-followups" />
            </div>

            <h3 className="admin-dashboard__section-title" style={{ marginTop: "1.5rem" }}>Colas operativas</h3>
            <ul className="admin-dashboard__queue-list">
              {(summary.queues || []).map((queue) => (
                <li key={queue.key}>
                  <span>{queue.label}</span>
                  <Link to={queue.href}>{queue.count} →</Link>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </main>
    </AdminPageLayout>
  );
}

export default function AdminInventoryQualityDashboardPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminInventoryQualityDashboardPageContent />
    </RequirePermission>
  );
}
